import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import {
  Email as EmailIcon,
  MarkEmailRead as VerifyIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(0); // 0: email entry, 1: email sent
  const [hasTriedVerification, setHasTriedVerification] = useState(false); // Prevent infinite loops
  const { requestVerification, verifyEmail } = useAuth();

  const handleVerification = useCallback(async (emailAddress: string, token: string) => {

    try {
      setIsLoading(true);
      setError(null);
      await verifyEmail(emailAddress.toLowerCase(), token);
      // AuthContext will handle redirect after successful verification
    } catch (err: any) {
      setError(err.message || 'Email verification failed');
    } finally {
      setIsLoading(false);
    }
  }, [verifyEmail]);

  // Check for verification token in URL and automatically verify
  useEffect(() => {
    if (hasTriedVerification) return; // Prevent multiple verification attempts

    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const emailParam = urlParams.get('email');
    
    if (token && emailParam) {
      setHasTriedVerification(true); // Mark that we've attempted verification
      
      // Clean up URL parameters to prevent re-triggering
      const newUrl = window.location.protocol + "//" + window.location.host + window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
      
      // Automatically verify if we have both token and email
      // This will handle the login and redirect through AuthContext
      handleVerification(decodeURIComponent(emailParam), token);
    }
  }, [hasTriedVerification, handleVerification]); // Include both dependencies

  const handleRequestVerification = async (e?: React.FormEvent | React.MouseEvent | React.KeyboardEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    setError(null);
    setSuccess(null);

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    if (!email.toLowerCase().endsWith('@bfh.ch')) {
      setError('Only @bfh.ch email addresses are allowed');
      return;
    }

    try {
      setIsLoading(true);
      const message = await requestVerification(email.toLowerCase());
      setSuccess(message);
      setStep(1);
    } catch (err: any) {
      setError(err.message || 'Failed to send verification email');
    } finally {
      setIsLoading(false);
    }
  };

  const steps = ['Enter Email', 'Check Email'];

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4
        }}
      >
        <Paper elevation={8} sx={{ p: 4, width: '100%', maxWidth: 500 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <SecurityIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom>
              Strategy Mapper
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Secure Access with Email Verification
            </Typography>
          </Box>

          <Stepper activeStep={step} alternativeLabel sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          {step === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Enter Your BFH Email
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                We'll send you a secure verification link to access the Strategy Mapper.
              </Typography>
              
              <TextField
                fullWidth
                type="email"
                label="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.name@bfh.ch"
                InputProps={{
                  startAdornment: <EmailIcon sx={{ mr: 1, color: 'action.active' }} />
                }}
                sx={{ mb: 3 }}
                disabled={isLoading}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isLoading && email) {
                    e.preventDefault();
                    handleRequestVerification(e as any);
                  }
                }}
              />

              <Box
                component="button"
                type="button"
                disabled={isLoading || !email}
                onClick={async (e: React.MouseEvent<HTMLButtonElement>) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (e.nativeEvent) {
                    e.nativeEvent.stopImmediatePropagation();
                  }
                  try {
                    await handleRequestVerification(e);
                  } catch (err) {
                    // Error is handled in handleRequestVerification
                  }
                }}
                sx={{
                  width: '100%',
                  mb: 2,
                  py: 1.5,
                  px: 3,
                  fontSize: '1rem',
                  fontWeight: 500,
                  color: 'white',
                  backgroundColor: (theme) => theme.palette.primary.main,
                  border: 'none',
                  borderRadius: 1,
                  cursor: isLoading || !email ? 'not-allowed' : 'pointer',
                  opacity: isLoading || !email ? 0.6 : 1,
                  '&:hover': {
                    backgroundColor: (theme) => isLoading || !email ? theme.palette.primary.main : theme.palette.primary.dark,
                  },
                  '&:disabled': {
                    cursor: 'not-allowed',
                  },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Send Verification Email'}
              </Box>

              <Typography variant="caption" display="block" color="text.secondary" sx={{ textAlign: 'center' }}>
                Only @bfh.ch email addresses are allowed
              </Typography>
            </Box>
          )}

          {step === 1 && (
            <Box sx={{ textAlign: 'center' }}>
              <VerifyIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Check Your Email
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                We've sent a login link to <strong>{email}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Click the link in the email to complete your login and access Strategy Mapper.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontStyle: 'italic' }}>
                You can safely close this page. Your login will be handled automatically when you click the link in your email. This may take a few minutes.
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
                <Button
                  variant="outlined"
                  onClick={() => setStep(0)}
                >
                  Change Email
                </Button>
                
                <Button
                  variant="contained"
                  onClick={handleRequestVerification}
                  disabled={isLoading}
                >
                  {isLoading ? <CircularProgress size={20} /> : 'Resend Email'}
                </Button>
              </Box>
            </Box>
          )}

          <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ textAlign: 'center' }}>
              🔒 <strong>Security Notice:</strong> Email verification is required for all users. 
              This ensures only authorized BFH personnel can access the Strategy Mapper tool.
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;